// src/components/session/StartChoiceDialog.js
// Start dialog: continue one of this browser's saved journeys (newest first) or start a new one.
import React from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton,
  ListItemText, Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return `${formatDate(iso)}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return "";
  }
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function StartChoiceDialog({ open, journeys = [], error, onContinue, onStartNew }) {
  const canContinue = journeys.length > 0;
  return (
    <Dialog open={open} maxWidth="xs" fullWidth aria-labelledby="start-choice-title">
      <DialogTitle id="start-choice-title" sx={{ fontWeight: 700 }}>
        {canContinue ? "Welcome back" : "Start a learning journey"}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        {canContinue && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Your journeys are saved. Pick one to continue. Saved journeys are deleted 30 days after the last visit.
            </Typography>
            <List
              dense
              aria-label="Saved journeys"
              sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, py: 0 }}
            >
              {journeys.map((j, i) => (
                <ListItemButton
                  key={j.ref}
                  onClick={() => onContinue(j.ref)}
                  autoFocus={i === 0}
                  divider={i < journeys.length - 1}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>Started {formatDateTime(j.created_at)}</Box>
                        {j.current && <Chip label="Last used" size="small" variant="outlined" sx={{ height: 20 }} />}
                      </Box>
                    }
                    secondary={`${j.steps_completed} of ${j.steps_total} steps completed · Expires ${formatDate(j.expires_at)}`}
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600, component: "div" }}
                  />
                  <PlayArrowIcon fontSize="small" color="action" sx={{ ml: 1 }} aria-hidden />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          fullWidth
          variant={canContinue ? "outlined" : "contained"}
          startIcon={<AddIcon />}
          onClick={onStartNew}
        >
          Start a new journey
        </Button>
      </DialogActions>
    </Dialog>
  );
}
